/**
 * Cognito password sign-in (ENG-6103).
 *
 * One wrapper over Amplify's sign-in state machine, so the four password-login
 * entry points (Login, SMBLoginForm, useEmailLogin, signup's auto-login) never
 * touch `aws-amplify/auth` directly. Every call answers with a discriminated
 * result: `done`, a `challenge` the UI must answer, or `unsupported` for a step
 * this app does not drive (custom auth, new-password-required, and so on).
 *
 * Failures are thrown as `CognitoSignInError`, whose `code` is one of the
 * webapp's existing api error codes, so forms branch on the same constants
 * they already use for the legacy Basic-auth login.
 *
 * The password is only ever passed straight to Amplify; nothing here keeps it.
 */
import {
  type SignInOutput,
  confirmSignIn as amplifyConfirmSignIn,
  signIn as amplifySignIn,
  fetchMFAPreference,
  fetchUserAttributes,
  setUpTOTP,
  signOut,
  updateMFAPreference,
  verifyTOTPSetup,
} from 'aws-amplify/auth';

import ERROR_CODES, { type ErrorCode } from '@/core/apiErrorCodes';

import { preferOnly } from './cognitoMfaPreference';
import {
  activateCognitoSession,
  ensureCognitoConfigured,
  resetCognitoSessionLocally,
} from './cognitoSession';

/** The Cognito challenge names, as the brief and the api docs spell them. */
export type CognitoChallenge =
  | 'SOFTWARE_TOKEN_MFA'
  | 'SMS_MFA'
  | 'EMAIL_OTP'
  | 'SELECT_MFA_TYPE'
  | 'MFA_SETUP';

export type CognitoMfaType = 'SMS' | 'TOTP' | 'EMAIL';

export interface TotpSetup {
  sharedSecret: string;
  /** `otpauth://` URI for authenticator apps. */
  setupUri: string;
}

export type CognitoSignInResult =
  | { type: 'done' }
  | { type: 'challenge'; challenge: 'SOFTWARE_TOKEN_MFA' }
  | { type: 'challenge'; challenge: 'SMS_MFA' | 'EMAIL_OTP'; destination?: string }
  | { type: 'challenge'; challenge: 'SELECT_MFA_TYPE'; allowedMfaTypes: CognitoMfaType[] }
  | {
      type: 'challenge';
      challenge: 'MFA_SETUP';
      /** Present when Cognito asked for TOTP setup; absent while a type must be chosen. */
      totp?: TotpSetup;
      allowedMfaTypes: CognitoMfaType[];
    }
  | { type: 'unsupported'; step: string };

/** A sign-in failure carrying the webapp error code the forms switch on. */
export class CognitoSignInError extends Error {
  readonly code: ErrorCode;
  declare readonly cause: unknown;

  constructor(code: ErrorCode, message: string, cause?: unknown) {
    super(message);
    this.name = 'CognitoSignInError';
    this.code = code;
    Object.defineProperty(this, 'cause', { value: cause, configurable: true, writable: true });
  }
}

/** Copy for a challenge session that lapsed (`auth.token_expired`). */
export const SIGN_IN_EXPIRED_MESSAGE = 'Your sign-in session expired. Please sign in again.';

/** Issuer label shown in authenticator apps. */
const TOTP_ISSUER = 'OnFrontiers';

const THROTTLED = new Set([
  'TooManyRequestsException',
  'LimitExceededException',
  'TooManyFailedAttemptsException',
]);

function errorName(err: unknown): string | undefined {
  return err instanceof Error ? err.name : undefined;
}

/**
 * Amplify's errors to the webapp's codes. Anything unmapped (network failures,
 * misconfiguration) is rethrown unchanged so callers report it to Sentry.
 */
export function mapError(err: unknown, phase: 'password' | 'code'): never {
  const name = errorName(err);
  const message = err instanceof Error ? err.message : '';

  if (name && THROTTLED.has(name)) {
    throw new CognitoSignInError(
      ERROR_CODES.AUTH_TOO_MANY_ATTEMPTS,
      'Too many sign-in attempts',
      err
    );
  }
  switch (name) {
    case 'NotAuthorizedException':
      // Cognito reports its own lockout as NotAuthorized with this message.
      if (/attempts exceeded/i.test(message)) {
        throw new CognitoSignInError(
          ERROR_CODES.AUTH_TOO_MANY_ATTEMPTS,
          'Too many sign-in attempts',
          err
        );
      }
      // While answering a challenge, NotAuthorized means the three-minute
      // challenge session lapsed: the user has to start again.
      if (phase === 'code') {
        throw new CognitoSignInError(ERROR_CODES.AUTH_TOKEN_EXPIRED, SIGN_IN_EXPIRED_MESSAGE, err);
      }
      throw new CognitoSignInError(
        ERROR_CODES.AUTH_INVALID_CREDENTIALS,
        'Invalid username or password',
        err
      );
    // User-existence errors are hidden by the pool; map them the same way in
    // case a pool is configured to reveal them. Same for a forced reset: the
    // invalid-credentials message carries the forgot-password link.
    case 'UserNotFoundException':
    case 'PasswordResetRequiredException':
      throw new CognitoSignInError(
        ERROR_CODES.AUTH_INVALID_CREDENTIALS,
        'Invalid username or password',
        err
      );
    case 'CodeMismatchException':
    case 'ExpiredCodeException':
    case 'EnableSoftwareTokenMFAException':
      throw new CognitoSignInError(ERROR_CODES.AUTH_OTP_INVALID, 'Invalid verification code', err);
    default:
      throw err;
  }
}

function toTotpSetup(
  details: { sharedSecret: string; getSetupUri(appName: string, accountName?: string): URL },
  accountName?: string
): TotpSetup {
  return {
    sharedSecret: details.sharedSecret,
    setupUri: details.getSetupUri(TOTP_ISSUER, accountName).toString(),
  };
}

// The account name an authenticator app lists the code under.
let pendingUsername: string | undefined;

/** Forget the sign-in in progress (its username). Amplify keeps the challenge. */
export function clearPendingSignIn(): void {
  pendingUsername = undefined;
}

async function toResult(output: SignInOutput): Promise<CognitoSignInResult> {
  const { nextStep } = output;
  switch (nextStep.signInStep) {
    case 'DONE':
      pendingUsername = undefined;
      // Make the session active before resolving, so the viewer fetch that
      // follows already sends the Cognito ID token.
      await activateCognitoSession();
      return { type: 'done' };
    case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
      return { type: 'challenge', challenge: 'SOFTWARE_TOKEN_MFA' };
    case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
      return {
        type: 'challenge',
        challenge: 'SMS_MFA',
        destination: nextStep.codeDeliveryDetails?.destination,
      };
    case 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE':
      return {
        type: 'challenge',
        challenge: 'EMAIL_OTP',
        destination: nextStep.codeDeliveryDetails?.destination,
      };
    case 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION':
      return {
        type: 'challenge',
        challenge: 'SELECT_MFA_TYPE',
        allowedMfaTypes: nextStep.allowedMFATypes ?? [],
      };
    case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP':
      return {
        type: 'challenge',
        challenge: 'MFA_SETUP',
        totp: toTotpSetup(nextStep.totpSetupDetails, pendingUsername),
        allowedMfaTypes: ['TOTP'],
      };
    case 'CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION':
      return {
        type: 'challenge',
        challenge: 'MFA_SETUP',
        allowedMfaTypes: nextStep.allowedMFATypes ?? [],
      };
    case 'RESET_PASSWORD':
      // Amplify turns PasswordResetRequiredException into this step instead of
      // throwing. Same answer as the thrown form: the reset link.
      pendingUsername = undefined;
      throw new CognitoSignInError(
        ERROR_CODES.AUTH_INVALID_CREDENTIALS,
        'Invalid username or password'
      );
    default:
      return { type: 'unsupported', step: nextStep.signInStep };
  }
}

/**
 * Start a password sign-in. A Cognito session already in storage is signed
 * out first (Amplify refuses to sign in over one), locally only, so other
 * devices keep theirs.
 */
export async function signIn(email: string, password: string): Promise<CognitoSignInResult> {
  if (!ensureCognitoConfigured()) {
    throw new Error('Cognito sign-in called while the cognito-auth flag is off');
  }
  // A sign-in replacing a live session (say, a restart after the viewer fetch
  // failed): drop the old one locally first, or a request or the scheduled
  // refresh during the MFA wait finds no SDK tokens and expires the session.
  resetCognitoSessionLocally();
  pendingUsername = email;
  const attempt = () =>
    amplifySignIn({
      username: email,
      password,
      options: { authFlowType: 'USER_PASSWORD_AUTH' },
    });
  try {
    return await toResult(await attempt());
  } catch (err) {
    if (errorName(err) !== 'UserAlreadyAuthenticatedException') mapError(err, 'password');
  }
  await signOut().catch(() => undefined);
  try {
    return await toResult(await attempt());
  } catch (err) {
    return mapError(err, 'password');
  }
}

/** Answer the pending challenge with a code (TOTP, SMS, email, or TOTP setup). */
export async function confirmSignIn(code: string): Promise<CognitoSignInResult> {
  try {
    return await toResult(await amplifyConfirmSignIn({ challengeResponse: code.trim() }));
  } catch (err) {
    return mapError(err, 'code');
  }
}

/** Answer SELECT_MFA_TYPE, or choose which factor to set up during MFA_SETUP. */
export async function selectMfaType(type: CognitoMfaType): Promise<CognitoSignInResult> {
  try {
    return await toResult(await amplifyConfirmSignIn({ challengeResponse: type }));
  } catch (err) {
    return mapError(err, 'code');
  }
}

/**
 * TOTP enrolment for a user who is already signed in to Cognito but has no
 * factor yet: the api answers `auth.mfa_setup_required` for users who had MFA
 * before the migration (decision Q1). Not for the MFA_SETUP challenge, which
 * carries its own secret and is answered with `confirmSignIn`.
 */
export async function startTotpEnrolment(accountName?: string): Promise<TotpSetup> {
  try {
    return toTotpSetup(await setUpTOTP(), accountName);
  } catch (err) {
    return mapError(err, 'code');
  }
}

/**
 * Verify the first TOTP code and make TOTP the user's preferred factor,
 * switching off the factors in `turnOff` in the same call (ENG-6323).
 */
export async function completeTotpEnrolment(
  code: string,
  turnOff?: CognitoMfaType[]
): Promise<void> {
  try {
    await verifyTOTPSetup({ code: code.trim() });
    await updateMFAPreference(preferOnly('TOTP', turnOff));
  } catch (err) {
    mapError(err, 'code');
  }
}

/** True when the signed-in user already has TOTP enrolled with Cognito. */
export async function hasCognitoTotp(): Promise<boolean> {
  const { enabled } = await fetchMFAPreference();
  return enabled?.includes('TOTP') ?? false;
}

/**
 * The signed-in user's email, for the authenticator app's account label. Read
 * from Cognito: the api's MFA gate refuses the viewer's email subtree.
 */
export async function cognitoEmail(): Promise<string | undefined> {
  const { email } = await fetchUserAttributes();
  return email;
}
